import Handlebars from 'handlebars';
import {
  scanMustaches,
  hbStr,
  escapeLiteralText
} from 'src/components/settings/templates/engine/transpile';

export const PREVIEW_MODES = Object.freeze({
  LABEL: 'label_mode',
  VALUE: 'value_mode'
});

export function tokenLabelsFor(option) {
  const aliases = Array.isArray(option.aliases) ? option.aliases : [];
  const seen = new Set();
  const out = [];
  for (const label of [option.label, ...aliases]) {
    if (!label) continue;
    if (seen.has(label)) continue;
    seen.add(label);
    out.push(label);
  }
  return out;
}

/**
 * Convert a fetched merge-field catalogue (groups of descriptors from
 * `fetchMergeFieldCatalogue`) into flat option-shaped objects that
 * `applyPreviewMode` can consume alongside the legacy `mergeFieldsOptions`.
 *
 * Each catalogue descriptor's canonical `token` becomes both the option `id`
 * (so callers can key their defaults by token) and the `label` (so
 * `tokenLabelsFor` will produce `{{token}}` for the substitution).
 * Collections (`kind: 'collection'`) are skipped — the FOR-EACH mechanism
 * handles those, and single-value preview substitution isn't meaningful.
 *
 * @param {Array<{group:string, fields:Array<object>}>} catalogueGroups
 * @returns {Array<{id:string,label:string,aliases:string[]}>}
 */
export function flattenCatalogueToOptions(catalogueGroups) {
  if (!Array.isArray(catalogueGroups)) return [];
  const out = [];
  for (const group of catalogueGroups) {
    const fields = (group && group.fields) || [];
    for (const f of fields) {
      if (!f || !f.token) continue;
      if (f.kind === 'collection') continue;
      out.push({
        id: f.token,
        label: f.token,
        aliases: Array.isArray(f.aliases) ? f.aliases : []
      });
    }
  }
  return out;
}

/**
 * Merge legacy `mergeFieldsOptions` with catalogue-derived options while
 * keeping legacy entries authoritative (their `id` is a stable camelCase key
 * that `getMergeFieldsDefaults` already writes to).
 *
 * A catalogue option is dropped when its canonical label duplicates any
 * legacy option's label OR any legacy option's alias — this prevents the
 * substitution from writing the same `{{Label}}` placeholder twice with
 * potentially different resolved values, which would corrupt the output.
 *
 * @param {Array<{id:string,label:string,aliases?:string[]}>} legacyOptions
 * @param {Array<{id:string,label:string,aliases?:string[]}>} catalogueOptions
 * @returns {Array<{id:string,label:string,aliases:string[]}>}
 */
export function mergeLegacyAndCatalogueOptions(
  legacyOptions,
  catalogueOptions
) {
  const legacy = Array.isArray(legacyOptions) ? legacyOptions : [];
  const catalogue = Array.isArray(catalogueOptions) ? catalogueOptions : [];
  const claimed = new Set();
  for (const opt of legacy) {
    for (const t of tokenLabelsFor(opt)) claimed.add(String(t).toLowerCase());
  }
  const merged = [...legacy];
  for (const opt of catalogue) {
    if (!opt || !opt.label) continue;
    const canonical = String(opt.label).toLowerCase();
    if (claimed.has(canonical)) continue;
    claimed.add(canonical);
    merged.push(opt);
  }
  return merged;
}

function buildLabelMap(options) {
  const map = new Map();
  for (const option of options || []) {
    const labels = tokenLabelsFor(option);
    if (!labels.length) continue;
    const canonicalLabel = labels[0];
    for (const label of labels) {
      if (!map.has(label)) map.set(label, { id: option.id, canonicalLabel });
    }
  }
  return map;
}

function renderValues(content, options, defaults) {
  const labelMap = buildLabelMap(options);

  let code = '';
  for (const span of scanMustaches(content)) {
    if (span.type === 'text') {
      code += escapeLiteralText(span.value);
      continue;
    }
    if (labelMap.has(span.inner)) {
      code += `{{lmPreview ${hbStr(span.inner)}}}`;
    } else {
      code += `\\${span.raw}`;
    }
  }

  const hb = Handlebars.create();
  hb.registerHelper('lmPreview', function lmPreview(label) {
    const info = labelMap.get(label);
    const canonicalPlaceholder = `{{${info ? info.canonicalLabel : label}}}`;
    const value = info ? defaults[info.id] : undefined;
    return new hb.SafeString(value || canonicalPlaceholder);
  });

  try {
    return hb.compile(code)({});
  } catch (e) {
    // Never destroy editor content if a template edge case fails to compile.
    return content;
  }
}

function renderLabels(content, options, defaults) {
  let next = content;
  for (const option of options || []) {
    const labels = tokenLabelsFor(option);
    if (!labels.length) continue;
    const value = defaults[option.id];
    if (!value) continue; // no resolved value → nothing to restore
    const canonicalPlaceholder = `{{${labels[0]}}}`;
    next = next.split(value).join(canonicalPlaceholder);
  }
  return next;
}

/**
 * Apply a preview mode to the editor content.
 *
 * Pure: no editor coupling, no DOM. Safe to unit-test.
 *
 * @param {string} content
 * @param {'label_mode'|'value_mode'} mode
 * @param {{ id: string, label: string, aliases?: string[] }[]} mergeFieldsOptions
 * @param {Record<string,string>} mergeFieldsDefaults  keyed by option `id`
 * @returns {string}
 */
export function applyPreviewMode(
  content,
  mode,
  mergeFieldsOptions,
  mergeFieldsDefaults
) {
  if (typeof content !== 'string' || !content) return content;
  if (mode !== PREVIEW_MODES.LABEL && mode !== PREVIEW_MODES.VALUE) {
    return content;
  }
  const defaults = mergeFieldsDefaults || {};
  if (mode === PREVIEW_MODES.VALUE) {
    return renderValues(content, mergeFieldsOptions || [], defaults);
  }
  return renderLabels(content, mergeFieldsOptions || [], defaults);
}
